import { Menu, Transition } from "@headlessui/react";
import { FC, Fragment } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { JobPostCompositeStatus } from "@utils/db/JobPost/getCompositeStatus";
import { ActionType } from "actions/actions";
import { NextRouter, useRouter } from "next/router";
import { JobPost } from "@prisma/client";
import { executeAction } from "@utils/actions/executeAction";

interface Props {
  jobPostId: JobPost["id"];
  compositeStatus: JobPostCompositeStatus;
  isJobPostDeletable: boolean;
}

const MoreMenuButton = () => (
  <Menu.Button className="focus:outline-none">
    <div className="inline-block items-center ml-2 px-3 py-2 border border-transparent shadow-sm font-medium rounded-md text-black bg-transparent border-black hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-offset-2">
      More
      <ChevronDownIcon className="inline h-4 w-4 ml-0.5 text-black" />
    </div>
  </Menu.Button>
);

export const getMoreMenuItems = ({
  jobPostId,
  compositeStatus,
  isJobPostDeletable,
  router,
}: Props & { router: NextRouter }) => {
  const items = [];

  if (compositeStatus === JobPostCompositeStatus.PUBLISHED) {
    items.push(
      <Menu.Item key="stop">
        {({ active }) => (
          <a
            href=""
            className="block px-4 py-2 text-black hover:bg-black/10"
            onClick={async (e) => {
              e.preventDefault();
              await executeAction({
                actionType: ActionType.JOB_POST__STOP,
                jobPostId,
              });
              router.reload();
            }}
          >
            Unpublish
          </a>
        )}
      </Menu.Item>
    );
  }

  if (compositeStatus === JobPostCompositeStatus.STOPPED) {
    items.push(
      <Menu.Item key="resume">
        {({ active }) => (
          <a
            href=""
            className="block px-4 py-2 text-black hover:bg-black/10"
            onClick={async (e) => {
              e.preventDefault();
              await executeAction({
                actionType: ActionType.JOB_POST__RESUME,
                jobPostId,
              });
              router.reload();
            }}
          >
            Publish
          </a>
        )}
      </Menu.Item>
    );
  }

  if (jobPostId && isJobPostDeletable) {
    items.push(
      <Menu.Item key="delete">
        {({ active }) => (
          <a
            href=""
            className="block px-4 py-2 text-red-600 hover:bg-black/10"
            onClick={async (e) => {
              e.preventDefault();
              await executeAction({
                actionType: ActionType.JOB_POST__DELETE,
                jobPostId,
              });
              router.push("/user/job-posts/my");
            }}
          >
            Delete
          </a>
        )}
      </Menu.Item>
    );
  }

  return items;
};

export const MoreMenu: FC<Props> = ({
  jobPostId,
  compositeStatus,
  isJobPostDeletable,
}) => {
  const router = useRouter();

  return (
    <Menu as="span" className="relative">
      {({ open }) => (
        <>
          <MoreMenuButton />
          <Transition
            show={open}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              static
              className="origin-top-right fixed right-0 mt-2 left-0 text-lg rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none sm:absolute sm:left-auto sm:w-40 sm:text-sm"
            >
              {getMoreMenuItems({
                jobPostId,
                compositeStatus,
                isJobPostDeletable,
                router,
              }).map((item) => item)}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};
